import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { 
  Table, 
  Grid, 
  Button, 
  Container, 
  Confirm, 
  Dimmer, 
  Loader, 
  Header, 
  Icon, 
  Segment 
} from 'semantic-ui-react';
import FuseContentModal from '../../../components/generic/FuseContentModal';
import * as KML_ACTIONS from '../../../actions/kmlUploadActions';
import KmlDetails from './KmlDetails';

// Constants
const PERMISSIONS = {
  KML_UPLOADS: 'KML Uploads',
  OTHER: 'Other'
};

const ACTIONS = {
  READ: 'READ',
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  ADMIN: 'admin'
};

// Utility Functions
const updateState = (setState, updates) => {
  setState(prevState => ({ ...prevState, ...updates }));
};

const hasPermissions = (userPermissions, category) => {
  return !_.isEmpty(userPermissions) && userPermissions.hasOwnProperty(category);
};

const isAdmin = (userPermissions) => {
  return hasPermissions(userPermissions, PERMISSIONS.OTHER) && 
         userPermissions[PERMISSIONS.OTHER].actions[ACTIONS.ADMIN];
};

const canReadKmlFile = (userPermissions) => {
  return (hasPermissions(userPermissions, PERMISSIONS.KML_UPLOADS) && 
          userPermissions[PERMISSIONS.KML_UPLOADS].actions[ACTIONS.READ]) || 
         isAdmin(userPermissions);
};

const canCreateKmlFile = (userPermissions) => {
  return (hasPermissions(userPermissions, PERMISSIONS.KML_UPLOADS) && 
          canReadKmlFile(userPermissions) && 
          userPermissions[PERMISSIONS.KML_UPLOADS].actions[ACTIONS.CREATE]) || 
         isAdmin(userPermissions);
};

const canUpdateKmlFile = (userPermissions) => {
  return (hasPermissions(userPermissions, PERMISSIONS.KML_UPLOADS) && 
          canReadKmlFile(userPermissions) && 
          userPermissions[PERMISSIONS.KML_UPLOADS].actions[ACTIONS.UPDATE]) || 
         isAdmin(userPermissions);
};

const canDeleteKmlFile = (userPermissions) => {
  return (hasPermissions(userPermissions, PERMISSIONS.KML_UPLOADS) && 
          canReadKmlFile(userPermissions) && 
          userPermissions[PERMISSIONS.KML_UPLOADS].actions[ACTIONS.DELETE]) || 
         isAdmin(userPermissions);
};

export function KmlUploadPage(props) {
  const [state, setState] = useState({
    selectedKml: {},
    isEditKmlOpen: false,
    deleteKmlCandidate: {},
    showConfirmDeleteKml: false,
  });

  useEffect(() => {
    if (!_.isEmpty(props.tenant) && props.isPermissionsFetched) {
      if (canReadKmlFile(props.userPermissions)) {
        props.fetchKmlFilesAction(props.tenant);
      } else {
        toast.error('You do not have permission to view uploaded KML Files');
      }
    }
  }, [props.tenant, props.isPermissionsFetched]);

  const handleKmlSelection = (rowId) => {
    const selectedKml = props.kmlFiles.find((kml) => kml.id === rowId);
    updateState(setState, { selectedKml: selectedKml || {} });
  };

  const handleDeleteKml = (e, kml) => {
    e.stopPropagation();
    updateState(setState, {
      deleteKmlCandidate: kml,
      showConfirmDeleteKml: true
    });
  };

  const confirmDeleteKml = () => {
    props.deleteKmlFileAction(state.deleteKmlCandidate, props.tenant);
    updateState(setState, {
      showConfirmDeleteKml: false,
      deleteKmlCandidate: {}
    });
  };

  const renderEmptyState = () => (
    <Segment>
      <Header icon>
        <Icon name='file' color="black" />
        No KML Files Uploaded
      </Header>
    </Segment>
  );

  const renderLoadingState = () => (
    <div style={{ height: '200px', margin: '10px 0px' }}>
      <Dimmer inverted active>
        <Loader content='Loading' />
      </Dimmer>
    </div>
  );

  const renderKmlTable = () => {
    const sortedKmlFiles = [...props.kmlFiles].sort((a, b) => a.name.localeCompare(b.name));

    return (
      <Segment>
        <Table compact selectable size='small'>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className='nectaPrimaryBg'>Name</Table.HeaderCell>
              <Table.HeaderCell className='nectaPrimaryBg'>Description</Table.HeaderCell>
              <Table.HeaderCell className='nectaPrimaryBg'>File Name</Table.HeaderCell>
              <Table.HeaderCell className='nectaPrimaryBg'>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedKmlFiles.map((kml, index) => (
              <Table.Row
                key={`${kml.id}.${index}`}
                active={kml.id === state.selectedKml.id}
                onClick={() => handleKmlSelection(kml.id)}
              >
                <Table.Cell>{kml.name}</Table.Cell>
                <Table.Cell>{kml.description}</Table.Cell>
                <Table.Cell>{kml.fileName}</Table.Cell>
                <Table.Cell>
                  {canUpdateKmlFile(props.userPermissions) && (
                    <FuseContentModal
                      header={`Edit KML File: ${kml.fileName}`}
                      content={<KmlDetails kml={kml} bdaction={'edit'} />}
                      trigger={<Button compact className="warning" icon='edit' size='mini' />}
                      onOpen={() => updateState(setState, {
                        isEditKmlOpen: true,
                        selectedKml: kml
                      })}
                      onClose={() => updateState(setState, { isEditKmlOpen: false })}
                    />
                  )}
                  {canDeleteKmlFile(props.userPermissions) && (
                    <Button 
                      compact 
                      className='danger' 
                      icon='trash' 
                      size='mini' 
                      onClick={(e) => handleDeleteKml(e, kml)} 
                    />
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Segment>
    );
  };

  const renderContent = () => {
    if (props.fetchingData) return renderLoadingState();
    if (!props.kmlFiles || props.kmlFiles.length === 0) return renderEmptyState();
    return renderKmlTable();
  };

  return (
    <Container fluid>
      <Confirm
        content={`Are you sure you want to delete the selected KML file: ${state.deleteKmlCandidate.fileName}?`}
        open={state.showConfirmDeleteKml}
        onCancel={() => updateState(setState, { showConfirmDeleteKml: false })}
        onConfirm={confirmDeleteKml}
      />

      <Grid>
        <Grid.Row>
          <Grid.Column width='8'>
            <h3>Uploaded KML Files:</h3>
          </Grid.Column>
          <Grid.Column width='8'>
            {canCreateKmlFile(props.userPermissions) && (
              <FuseContentModal
                header="Upload KML File"
                content={<KmlDetails kml={state.selectedKml} bdaction={'add'} />}
                trigger={
                  <Button 
                    compact 
                    className="success" 
                    icon='add' 
                    size='mini' 
                    floated='right'
                    content="Upload KML"
                  />
                }
                onClose={() => updateState(setState, {
                  kml: state.kml,
                  kmlFiles: props.kmlFiles
                })}
              />
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
      
      {renderContent()}
    </Container>
  );
}

const mapStateToProps = (state) => ({
  fetchingData: state.fetchingData === true,
  tenant: state.tenantManagement.activeTenant,
  kmlFiles: state.kmlFiles,
  userPermissions: _.isEmpty(state.authUserRoles.permissions) 
    ? {} 
    : state.authUserRoles.permissions,
  isPermissionsFetched: state.isPermissionsFetched === true,
});

export default connect(
  mapStateToProps,
  (dispatch) => ({
    fetchKmlFilesAction: (tenant) => dispatch(KML_ACTIONS.fetchKmlFilesAction(tenant)),
    deleteKmlFileAction: (file, tenant) => dispatch(KML_ACTIONS.deleteKmlFileAction(file, tenant)),
  })
)(KmlUploadPage);
